// FT-193 STEP-18 (REQ-08, REQ-11, REQ-12, CTR-04, CTR-08): общие куски интерфейса привязанной карты Т-Банка.
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { v4 as uuidv4 } from "uuid";
import { isMoneyDeclined } from "@/utils/moneyConfirmGuard";

// Карта клиента привязана: признак tinkoff_card_bound от сервера. Маска tinkoff_card_number служит только показу,
// признаком не служит; токены карты (tinkoff_rebill_id и др.) сотруднику не отдаются.
export function isTinkoffCardBound(client) {
  return !!(client && client.tinkoff_card_bound);
}

// Ключ идемпотентности ручного списания с карты (CTR-08): UUID v4 пакета uuid из байтов crypto.getRandomValues
// (есть и вне защищённого контекста, и в WebView). Math.random как запасной путь не годится: ключ защищает деньги
// от дубля, ему нужна настоящая случайность, поэтому без getRandomValues — исключение.
export function generateIdempotencyKey(cryptoApi = typeof crypto !== "undefined" ? crypto : null) {
  if (!cryptoApi || typeof cryptoApi.getRandomValues !== "function") {
    throw new Error("Нет источника случайных чисел для ключа идемпотентности");
  }
  return uuidv4({ rng: () => cryptoApi.getRandomValues(new Uint8Array(16)) });
}

// FT-193 W1: незавершённое списание с карты — ключ идемпотентности и неизменный снимок тела запроса — хранится в
// sessionStorage по booking.id, чтобы перезагрузка страницы его не теряла: повтор после неё уходит с тем же ключом и
// тем же телом. Хранилище может быть недоступно (приватный режим, запрет сайта) или бросать на записи — всё в try/catch:
// без него остаётся снимок в памяти карточки.
const PENDING_CHARGE_PREFIX = "ft193.pendingCardCharge.";

function sessionStorageOrNull() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function readPendingCharge(bookingId, storage = sessionStorageOrNull()) {
  if (!bookingId || !storage) return null;
  try {
    const raw = storage.getItem(PENDING_CHARGE_PREFIX + bookingId);
    if (!raw) return null;
    const pending = JSON.parse(raw);
    // Без ключа и сумм снимок повторить нельзя — такой не подхватываем.
    if (!pending || typeof pending.idempotency_key !== "string" || !pending.departure_payments) return null;
    return pending;
  } catch {
    return null;
  }
}

export function writePendingCharge(bookingId, pending, storage = sessionStorageOrNull()) {
  if (!bookingId || !storage) return;
  try {
    storage.setItem(PENDING_CHARGE_PREFIX + bookingId, JSON.stringify(pending));
  } catch {
    // квота или запрет хранилища: снимок остаётся в памяти карточки
  }
}

// Стирается только запись с тем же ключом: поздний ответ старой карточки (другой экземпляр компонента, тот же booking.id)
// не должен стереть запись нового платежа — иначе после потери ответа на него перезагрузка оставит повтор без ключа.
export function clearPendingCharge(bookingId, key, storage = sessionStorageOrNull()) {
  if (!bookingId || !key || !storage) return;
  try {
    const stored = readPendingCharge(bookingId, storage);
    if (stored && stored.idempotency_key !== key) return;
    storage.removeItem(PENDING_CHARGE_PREFIX + bookingId);
  } catch {
    // хранилище недоступно — стирать нечего
  }
}

// Страница оплаты привязки карты. Натив: @capacitor/browser (iOS SFSafariViewController / Android Custom Tabs) —
// window.open в WebView приложения уводит на внешнюю вкладку без возврата. Веб: новая вкладка; запрос асинхронный,
// и блокировщик всплывающих окон может её не пустить — тогда открываем в текущей вкладке, а не теряем ссылку.
export function openPaymentUrl(url, deps = {}) {
  const isNative = deps.isNative || (() => Capacitor.isNativePlatform());
  const browser = deps.browser || Browser;
  const win = deps.win || window;
  if (isNative()) {
    return browser.open({ url });
  }
  const opened = win.open(url, "_blank");
  if (!opened) win.location.href = url;
  return undefined;
}

// Исход запроса неизвестен: ответа нет (сеть, таймаут клиента), сервер ответил 5xx/408 — запись могла пройти, —
// или 409 locked: первый запрос с тем же ключом идемпотентности ещё выполняется. Ключ в этих случаях сохраняется,
// повтор уходит с ним же.
// Однозначны только 2xx и 4xx (422, 406, 403, 404): сервер ответил и отказал до записи.
// Отказ оператора подтвердить крупную сумму (moneyConfirmGuard) — запрос не уходил, исход известен: ничего не записано.
export function isOutcomeUnknown(error) {
  if (isMoneyDeclined(error)) return false;
  const status = error && error.response && error.response.status;
  return !status || status >= 500 || status === 408 || status === 409;
}
